import { create } from 'zustand';
import * as CertificationService from '@/services/AdminService/AdminCertificationService';
import type {
  Certification,
  CertificationWithCourses,
  ActiveCourse,
  CertificationPayload,
  PaginationInfo,
  LearnerProgress,
  ExpiryRenewal,
} from '@/services/AdminService/AdminCertificationService';
import * as UserCertificateService from '@/services/PagesServices/CertificationManagementService';
import type {
  User,
  CertificateTemplate,
  IssuedCertificate,
} from '@/services/PagesServices/CertificationManagementService';
import * as ExternalCertificateService from '@/services/AdminService/ExternalCertificateService';
import type {
  ExternalCertificate,
} from '@/services/AdminService/ExternalCertificateService';

interface CertificationManagementState {
  // Data
  certifications: Certification[];
  certificationDetail: CertificationWithCourses | null;
  activeCourses: ActiveCourse[];
  learnerProgress: LearnerProgress[];
  expiryRenewals: ExpiryRenewal[];
  pagination: PaginationInfo | null;
  learnerProgressPagination: PaginationInfo | null;
  expiryRenewalsPagination: PaginationInfo | null;

  // User Certificates Data
  users: User[];
  certificateTemplates: CertificateTemplate[];
  issuedCertificates: IssuedCertificate[];
  issuedCertificatesTotalPages: number;
  issuedCertificatesCurrentPage: number;

  // External Certificates Data
  externalCertificates: ExternalCertificate[];
  externalCertificatesTotalPages: number;
  externalCertificatesCurrentPage: number;
  externalCertificatesStats: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  } | null;

  // UI State
  loading: boolean;
  error: string | null;
  selectedCertification: Certification | null;

  // Actions - Certifications
  fetchCertifications: (page?: number, limit?: number, status?: 'active' | 'inactive' | 'all', search?: string) => Promise<void>;
  fetchCertificationById: (certificationId: number) => Promise<void>;
  fetchActiveCourses: () => Promise<void>;
  createCertification: (data: CertificationPayload) => Promise<{ success: boolean; certificationId?: number }>;
  updateCertification: (certificationId: number, data: Partial<CertificationPayload>) => Promise<{ success: boolean }>;
  deleteCertification: (certificationId: number) => Promise<{ success: boolean }>;
  toggleStatus: (certificationId: number) => Promise<{ success: boolean }>;
  addCourseRequirement: (certificationId: number, courseId: number) => Promise<{ success: boolean }>;
  removeCourseRequirement: (certificationId: number, courseId: number) => Promise<{ success: boolean }>;

  // Actions - Learner Progress & Tracking
  fetchLearnerProgress: (certificationId?: number | null, status?: string | null, search?: string | null, page?: number, limit?: number) => Promise<void>;

  // Actions - Expiry & Renewals
  fetchExpiryRenewals: (filter?: 'expiring_soon' | 'expired' | 'all', page?: number, limit?: number) => Promise<void>;

  // Actions - Enrollment
  enrollStudent: (certificationId: number, userId: string) => Promise<{ success: boolean }>;
  issueCertificate: (enrollmentId: number) => Promise<{ success: boolean; message?: string }>;

  // Actions - Certificate Viewing & Downloading
  viewCertificate: (enrollmentId: number) => Promise<void>;
  downloadCertificate: (enrollmentId: number) => Promise<void>;

  // Actions - User Certificates
  fetchUsers: (search: string) => Promise<void>;
  fetchCertificateTemplates: () => Promise<void>;
  issueCertificateToUser: (certificateData: any) => Promise<{ success: boolean; certificate?: any; message?: string }>;
  fetchIssuedCertificates: (page?: number, limit?: number, search?: string) => Promise<void>;
  deleteCertificate: (certificateId: number) => Promise<{ success: boolean; message?: string }>;

  // Actions - External Certificates
  fetchExternalCertificates: (status?: 'all' | 'pending' | 'approved' | 'rejected', page?: number, limit?: number, search?: string) => Promise<void>;
  approveExternalCertificate: (certificateId: number) => Promise<{ success: boolean; message?: string }>;
  rejectExternalCertificate: (certificateId: number) => Promise<{ success: boolean; message?: string }>;
  fetchExternalCertificateStats: () => Promise<void>;

  // UI Actions
  setSelectedCertification: (certification: Certification | null) => void;
  clearError: () => void;
}

const useCertificationManagementStore = create<CertificationManagementState>((set, get) => ({
  // Initial state
  certifications: [],
  certificationDetail: null,
  activeCourses: [],
  learnerProgress: [],
  expiryRenewals: [],
  pagination: null,
  learnerProgressPagination: null,
  expiryRenewalsPagination: null,
  users: [],
  certificateTemplates: [],
  issuedCertificates: [],
  issuedCertificatesTotalPages: 1,
  issuedCertificatesCurrentPage: 1,
  externalCertificates: [],
  externalCertificatesTotalPages: 1,
  externalCertificatesCurrentPage: 1,
  externalCertificatesStats: null,
  loading: false,
  error: null,
  selectedCertification: null,

  // Fetch all certifications with pagination and filters
  fetchCertifications: async (
    page: number = 1,
    limit: number = 10,
    status: 'active' | 'inactive' | 'all' = 'all',
    search?: string
  ) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.getAllCertifications(page, limit, status, search);

      if (response.success && response.data) {
        set({
          certifications: response.data.data,
          pagination: response.data.pagination,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch certifications',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // Fetch single certification with details
  fetchCertificationById: async (certificationId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.getCertificationById(certificationId);

      if (response.success && response.data) {
        set({
          certificationDetail: response.data,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch certification details',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // Fetch active courses for dropdown
  fetchActiveCourses: async () => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.getActiveCourses();

      if (response.success && response.data) {
        set({
          activeCourses: response.data,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch active courses',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // Create new certification
  createCertification: async (data: CertificationPayload) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.createCertification(data);

      if (response.success && response.data) {
        set({ loading: false });
        // Refresh the list
        await get().fetchCertifications();
        return { success: true, certificationId: response.data.certificationId };
      } else {
        set({
          error: response.error?.message || 'Failed to create certification',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Update certification
  updateCertification: async (certificationId: number, data: Partial<CertificationPayload>) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.updateCertification(certificationId, data);

      if (response.success) {
        set({ loading: false });
        // Refresh the list
        await get().fetchCertifications();
        return { success: true };
      } else {
        set({
          error: response.error?.message || 'Failed to update certification',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Delete certification
  deleteCertification: async (certificationId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.deleteCertification(certificationId);

      if (response.success) {
        set({ loading: false });
        // Refresh the list
        await get().fetchCertifications();
        return { success: true };
      } else {
        set({
          error: response.error?.message || 'Failed to delete certification',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Toggle certification status
  toggleStatus: async (certificationId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.toggleCertificationStatus(certificationId);

      if (response.success) {
        set({ loading: false });
        // Refresh the list
        await get().fetchCertifications();
        return { success: true };
      } else {
        set({
          error: response.error?.message || 'Failed to toggle status',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Add course requirement
  addCourseRequirement: async (certificationId: number, courseId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.addCourseRequirement(certificationId, courseId);

      if (response.success) {
        set({ loading: false });
        // Refresh certification details
        await get().fetchCertificationById(certificationId);
        return { success: true };
      } else {
        set({
          error: response.error?.message || 'Failed to add course requirement',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Remove course requirement
  removeCourseRequirement: async (certificationId: number, courseId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.removeCourseRequirement(certificationId, courseId);

      if (response.success) {
        set({ loading: false });
        // Refresh certification details
        await get().fetchCertificationById(certificationId);
        return { success: true };
      } else {
        set({
          error: response.error?.message || 'Failed to remove course requirement',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Set selected certification
  setSelectedCertification: (certification: Certification | null) => {
    set({ selectedCertification: certification });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // ============================================================================
  // LEARNER PROGRESS & TRACKING
  // ============================================================================

  // Fetch learner progress
  fetchLearnerProgress: async (
    certificationId?: number | null,
    status?: string | null,
    search?: string | null,
    page: number = 1,
    limit: number = 10
  ) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.getLearnerProgress(
        certificationId,
        status,
        search,
        page,
        limit
      );

      if (response.success && response.data) {
        set({
          learnerProgress: response.data.data,
          learnerProgressPagination: response.data.pagination,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch learner progress',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // ============================================================================
  // EXPIRY & RENEWALS
  // ============================================================================

  // Fetch expiry renewals
  fetchExpiryRenewals: async (
    filter: 'expiring_soon' | 'expired' | 'all' = 'expiring_soon',
    page: number = 1,
    limit: number = 10
  ) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.getExpiryRenewals(filter, page, limit);

      if (response.success && response.data) {
        set({
          expiryRenewals: response.data.data,
          expiryRenewalsPagination: response.data.pagination,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch expiry renewals',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // ============================================================================
  // ENROLLMENT MANAGEMENT
  // ============================================================================

  // Enroll student in certification
  enrollStudent: async (certificationId: number, userId: string) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.enrollStudent(certificationId, userId);

      if (response.success) {
        set({ loading: false });
        // Refresh learner progress
        await get().fetchLearnerProgress();
        return { success: true };
      } else {
        set({
          error: response.error?.message || 'Failed to enroll student',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Issue certificate to student
  issueCertificate: async (enrollmentId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await CertificationService.issueCertificate(enrollmentId);

      if (response.success && response.data) {
        set({ loading: false });
        // Refresh learner progress
        await get().fetchLearnerProgress();
        return {
          success: true,
          message: response.data.message,
        };
      } else {
        set({
          error: response.error?.message || response.data?.message || 'Failed to issue certificate',
          loading: false,
        });
        return {
          success: false,
          message: response.error?.message || response.data?.message,
        };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // ============================================================================
  // CERTIFICATE VIEWING & DOWNLOADING
  // ============================================================================

  // View certificate (opens PDF inline in new tab)
  viewCertificate: async (enrollmentId: number) => {
    try {
      set({ loading: true, error: null });
      await CertificationService.viewCertificate(enrollmentId);
      set({ loading: false });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to view certificate',
        loading: false,
      });
    }
  },

  // Download certificate (downloads PDF file)
  downloadCertificate: async (enrollmentId: number) => {
    try {
      set({ loading: true, error: null });
      await CertificationService.downloadCertificate(enrollmentId);
      set({ loading: false });
    } catch (error: any) {
      set({
        error: error.message || 'Failed to download certificate',
        loading: false,
      });
    }
  },

  // ============================================================================
  // USER CERTIFICATES
  // ============================================================================

  // Fetch users for dropdown
  fetchUsers: async (search: string) => {
    try {
      const response = await UserCertificateService.fetchUsers(search);

      if (response.success && response.users) {
        set({ users: response.users });
      } else {
        set({ error: response.error?.message || 'Failed to fetch users' });
      }
    } catch (error: any) {
      set({ error: error.message || 'An error occurred' });
    }
  },

  // Fetch certificate templates
  fetchCertificateTemplates: async () => {
    try {
      const response = await UserCertificateService.fetchCertificateTemplates();

      if (response.success && response.templates) {
        set({ certificateTemplates: response.templates });
      } else {
        set({ error: response.error?.message || 'Failed to fetch certificate templates' });
      }
    } catch (error: any) {
      set({ error: error.message || 'An error occurred' });
    }
  },

  // Issue certificate to user
  issueCertificateToUser: async (certificateData: any) => {
    try {
      set({ loading: true, error: null });
      const response = await UserCertificateService.issueCertificateToUser(certificateData);

      if (response.success) {
        set({ loading: false });
        return {
          success: true,
          certificate: response.certificate,
          message: response.message,
        };
      } else {
        set({
          error: response.error?.message || 'Failed to issue certificate',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // Fetch issued certificates with pagination
  fetchIssuedCertificates: async (page: number = 1, limit: number = 10, search: string = '') => {
    try {
      set({ loading: true, error: null });
      const response = await UserCertificateService.fetchIssuedCertificates(page, limit, search);

      if (response.success && response.data && response.pagination) {
        set({
          issuedCertificates: response.data,
          issuedCertificatesTotalPages: response.pagination.totalPages,
          issuedCertificatesCurrentPage: response.pagination.page,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch issued certificates',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // Delete certificate
  deleteCertificate: async (certificateId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await UserCertificateService.deleteCertificate(certificateId);

      if (response.success) {
        set({ loading: false });
        return {
          success: true,
          message: response.message,
        };
      } else {
        set({
          error: response.error?.message || 'Failed to delete certificate',
          loading: false,
        });
        return { success: false };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return { success: false };
    }
  },

  // ============================================================================
  // EXTERNAL CERTIFICATES
  // ============================================================================

  // Fetch external certificates
  fetchExternalCertificates: async (
    status: 'all' | 'pending' | 'approved' | 'rejected' = 'all',
    page: number = 1,
    limit: number = 10,
    search: string = ''
  ) => {
    try {
      set({ loading: true, error: null });
      const response = await ExternalCertificateService.getAllExternalCertificates(
        status,
        page,
        limit,
        search
      );

      if (response.success && response.data) {
        set({
          externalCertificates: response.data.data,
          externalCertificatesTotalPages: response.data.pagination.totalPages,
          externalCertificatesCurrentPage: response.data.pagination.page,
          loading: false,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch external certificates',
          loading: false,
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
    }
  },

  // Approve external certificate
  approveExternalCertificate: async (certificateId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await ExternalCertificateService.approveCertificate(certificateId);

      if (response.success) {
        set({ loading: false });
        return {
          success: true,
          message: response.message || 'Certificate approved successfully',
        };
      } else {
        set({
          error: response.error?.message || 'Failed to approve certificate',
          loading: false,
        });
        return {
          success: false,
          message: response.error?.message,
        };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Reject external certificate
  rejectExternalCertificate: async (certificateId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await ExternalCertificateService.rejectCertificate(certificateId);

      if (response.success) {
        set({ loading: false });
        return {
          success: true,
          message: response.message || 'Certificate rejected successfully',
        };
      } else {
        set({
          error: response.error?.message || 'Failed to reject certificate',
          loading: false,
        });
        return {
          success: false,
          message: response.error?.message,
        };
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
        loading: false,
      });
      return {
        success: false,
        message: error.message,
      };
    }
  },

  // Fetch external certificate statistics
  fetchExternalCertificateStats: async () => {
    try {
      const response = await ExternalCertificateService.getCertificateStats();

      if (response.success && response.data) {
        set({
          externalCertificatesStats: response.data,
        });
      } else {
        set({
          error: response.error?.message || 'Failed to fetch statistics',
        });
      }
    } catch (error: any) {
      set({
        error: error.message || 'An error occurred',
      });
    }
  },
}));

export default useCertificationManagementStore;
